import { createContext, type PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { type UserResponse, useUserQuery } from "@/modules/user/api/useUser.ts";
import { activateLocale } from "@/locales/utils.ts";

interface MeData extends UserResponse {
  isPending: boolean;
  isError: boolean;
}

const meDefault: MeData = {
  firstName: "",
  lastName: "",
  email: "",
  locale: undefined,
  phoneNumber: "",
  role: "admin",
  isPending: false,
  isError: false,
};

export function MeDataProvider({ children }: PropsWithChildren) {
  const { data, isPending, isError } = useUserQuery();

  const value = useMemo(
    () => ({
      firstName: data?.firstName,
      lastName: data?.lastName,
      email: data?.email ?? "",
      phoneNumber: data?.phoneNumber,
      locale: data?.locale,
      role: data?.role ?? "admin",
      isPending: isPending,
      isError: isError,
    }),
    [data?.email, data?.firstName, data?.lastName, data?.locale, data?.phoneNumber, data?.role, isError, isPending],
  );

  useEffect(() => activateLocale(value.locale), [value.locale]);

  return <MeCtx.Provider value={value}>{children}</MeCtx.Provider>;
}

export const MeCtx = createContext<MeData>(meDefault);

export const useMeData = () => useContext(MeCtx);
